import React from "react";
import PropTypes from "prop-types";
import { Box, Flex, Heading } from "theme-ui";
import { Icon } from "../atoms";
import { colors, typography } from "../../styles/theme";

const defaultAccent = colors.secondaryOrange500;
const defaultBorder = colors.secondaryOrange200;

const getBulletLiStyles = (accentColor) => ({
  position: "relative",
  pl: "1.5rem",
  mb: "0.75rem",
  ...typography.textMd400,
  "&:last-child": { mb: 0 },
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: "0.55rem",
    width: "0.5rem",
    height: "0.5rem",
    borderRadius: "50%",
    backgroundColor: accentColor,
  },
});

const QuickSummary = ({
  id,
  heading,
  headingHtml,
  iconName,
  accentColor,
  borderColor,
  items,
  children,
  customStyles,
}) => {
  const accent = accentColor || defaultAccent;
  const border = borderColor || defaultBorder;
  const bulletLiStyles = getBulletLiStyles(accent);
  const hasItems = Array.isArray(items) && items.length > 0;

  return (
    <Box
      id={id}
      sx={{
        ...mainContainerStyles,
        border: `2px solid ${border}`,
        "& ul > li": bulletLiStyles,
        ...customStyles,
      }}
    >
      {(heading || headingHtml) && (
        <Flex
          sx={{
            alignItems: "center",
            gap: "0.5rem",
            mb: hasItems || children ? "1.25rem" : 0,
          }}
        >
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
            <Icon name={iconName} color={accent} size={40} />
          </Box>
          {headingHtml ? (
            <Box
              sx={headingStyles}
              dangerouslySetInnerHTML={{ __html: headingHtml }}
            />
          ) : (
            <Heading as="h2" sx={{ ...typography.h3, m: 0, flex: 1 }}>
              {heading}
            </Heading>
          )}
        </Flex>
      )}
      {hasItems && (
        <Box
          as="ul"
          sx={{
            ...bulletStyles,
            "& > li": bulletLiStyles,
          }}
        >
          {items.map((item, index) => (
            <li key={`quick-summary-item-${index}`}>{item}</li>
          ))}
        </Box>
      )}
      {children}
    </Box>
  );
};

const { mainContainerStyles, headingStyles, bulletStyles } = {
  mainContainerStyles: {
    borderRadius: "0.75rem",
    backgroundColor: colors.white,
    p: ["1.5rem", "1.5rem", "1.75rem", "2rem"],
    mb: ["3rem", "3rem", "4.5rem", "4.5rem"],
    "& p": { m: 0 },
    "& ul": {
      m: 0,
      p: 0,
      listStyle: "none",
    },
    mt: ["3rem", "3rem", "3rem", "6rem"],
  },
  headingStyles: {
    ...typography.h3,
    m: 0,
    flex: 1,
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      ...typography.h3,
      m: 0,
    },
    "& p": { m: 0 },
  },
  bulletStyles: {
    m: 0,
    p: 0,
    listStyle: "none",
  },
};

QuickSummary.defaultProps = {
  id: undefined,
  heading: undefined,
  headingHtml: undefined,
  iconName: "Zap",
  accentColor: defaultAccent,
  borderColor: defaultBorder,
  items: undefined,
  children: null,
  customStyles: {},
};

QuickSummary.propTypes = {
  id: PropTypes.string,
  heading: PropTypes.node,
  headingHtml: PropTypes.string,
  iconName: PropTypes.string,
  accentColor: PropTypes.string,
  borderColor: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.node),
  children: PropTypes.node,
  customStyles: PropTypes.object,
};

export default QuickSummary;
